import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Users, Plus, Search, GraduationCap, Briefcase, TrendingUp, 
  Building2, MapPin, Mail, Phone, Linkedin, Edit, Trash2,
  Download, Upload, RefreshCw, BookOpen, Award, MessageCircle, Copy, Key
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AlumniManagement() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'list';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [alumni, setAlumni] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [editingAlumni, setEditingAlumni] = useState(null);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordAlumni, setResetPasswordAlumni] = useState(null);
  const [resetPasswordResult, setResetPasswordResult] = useState(null);
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [passingYear, setPassingYear] = useState(new Date().getFullYear());
  
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    phone: '',
    gender: '',
    course_name: '',
    batch_year: '',
    passing_year: new Date().getFullYear(),
    roll_number: '',
    registration_number: '',
    current_status: 'unknown',
    current_organization: '',
    designation: '',
    location: '',
    linkedin_url: '',
    package_lpa: ''
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchAlumni();
    fetchStats();
  }, [search, statusFilter, yearFilter]);

  const fetchAlumni = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (yearFilter && yearFilter !== 'all') params.append('passing_year', yearFilter);
      
      const response = await axios.get(`${API_URL}/api/alumni/?${params}`, { headers });
      setAlumni(response.data.alumni || []);
    } catch (err) {
      toast.error('Failed to fetch alumni');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/alumni/stats`, { headers });
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats');
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/students?status=Active&limit=500`, { headers });
      setStudents(response.data.students || []);
    } catch (err) {
      toast.error('Failed to fetch students');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        passing_year: parseInt(formData.passing_year),
        package_lpa: formData.package_lpa ? parseFloat(formData.package_lpa) : null
      };

      if (editingAlumni) {
        await axios.put(`${API_URL}/api/alumni/${editingAlumni.id}`, payload, { headers });
        toast.success('Alumni updated successfully');
      } else {
        const response = await axios.post(`${API_URL}/api/alumni/`, payload, { headers });
        toast.success('Alumni added successfully');
        
        // Show credentials modal for new alumni
        if (response.data.login_credentials) {
          setCredentials({
            ...response.data.login_credentials,
            alumniName: formData.full_name,
            phone: formData.phone
          });
          setShowCredentialsModal(true);
        }
      }
      
      setShowAddModal(false);
      setEditingAlumni(null);
      resetForm();
      fetchAlumni();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this alumni?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/alumni/${id}`, { headers });
      toast.success('Alumni deleted');
      fetchAlumni();
      fetchStats();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleConvertStudents = async () => {
    if (selectedStudents.length === 0) {
      toast.error('Select at least one student');
      return;
    }
    
    try {
      const response = await axios.post(`${API_URL}/api/alumni/convert-from-students`, {
        student_ids: selectedStudents,
        passing_year: parseInt(passingYear)
      }, { headers });
      
      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => toast.warning(err));
      }
      
      setShowConvertModal(false);
      setSelectedStudents([]);
      fetchAlumni();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Conversion failed');
    }
  };

  const resetForm = () => {
    setFormData({
      full_name: '',
      email: '',
      password: '',
      phone: '',
      gender: '',
      course_name: '',
      batch_year: '',
      passing_year: new Date().getFullYear(),
      roll_number: '',
      registration_number: '',
      current_status: 'unknown',
      current_organization: '',
      designation: '',
      location: '',
      linkedin_url: '',
      package_lpa: ''
    });
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  // WhatsApp share function
  const shareViaWhatsApp = (email, password, alumniName, contactPhone = null) => {
    const message = `🎓 *Alumni Portal Login Credentials*

*Name:* ${alumniName}

*Login URL:* ${window.location.origin}/alumni-portal

*Email:* ${email}
*Password:* ${password}

⚠️ Please change your password after first login.

Thank you!`;
    
    const encodedMessage = encodeURIComponent(message);
    
    // If contact phone is provided, open WhatsApp with that number
    if (contactPhone) {
      // Remove any non-numeric characters and add country code if not present
      let phone = contactPhone.replace(/\D/g, '');
      if (!phone.startsWith('91') && phone.length === 10) {
        phone = '91' + phone;
      }
      window.open(`https://wa.me/${phone}?text=${encodedMessage}`, '_blank');
    } else {
      // Open WhatsApp without pre-filled number
      window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
    }
    toast.success('Opening WhatsApp...');
  };

  // Handle share credentials for existing alumni
  const handleShareCredentials = async (alum) => {
    try {
      toast.loading('Generating new credentials...');
      const response = await axios.post(
        `${API_URL}/api/alumni/${alum.id}/regenerate-credentials`,
        {},
        { headers }
      );
      toast.dismiss();
      
      if (response.data.login_credentials) {
        const { email, password } = response.data.login_credentials;
        shareViaWhatsApp(
          email,
          password,
          response.data.alumni_name || alum.full_name,
          response.data.phone || alum.phone
        );
      }
    } catch (error) {
      toast.dismiss();
      console.error('Error regenerating credentials:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate credentials');
    }
  };

  // Handle admin reset password
  const handleResetPassword = async (alum) => {
    setResetPasswordAlumni(alum);
    setResetPasswordResult(null);
    setShowResetPasswordModal(true);
  };

  const confirmResetPassword = async () => {
    try {
      toast.loading('Resetting password...');
      const response = await axios.post(
        `${API_URL}/api/alumni-portal/admin/reset-password/${resetPasswordAlumni.id}`,
        {},
        { headers }
      );
      toast.dismiss();
      
      setResetPasswordResult({
        password: response.data.new_password,
        name: response.data.alumni_name || resetPasswordAlumni.full_name,
        email: response.data.alumni_email || resetPasswordAlumni.email,
        phone: response.data.alumni_phone || resetPasswordAlumni.phone
      });
      toast.success('Password reset successfully');
    } catch (error) {
      toast.dismiss();
      console.error('Error resetting password:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset password');
      setShowResetPasswordModal(false);
    }
  };

  const openEditModal = (alum) => {
    setEditingAlumni(alum);
    setFormData({
      full_name: alum.full_name || '',
      email: alum.email || '',
      password: '',
      phone: alum.phone || '',
      gender: alum.gender || '',
      course_name: alum.course_name || '',
      batch_year: alum.batch_year || '',
      passing_year: alum.passing_year || new Date().getFullYear(),
      roll_number: alum.roll_number || '',
      registration_number: alum.registration_number || '',
      current_status: alum.current_status || 'unknown',
      current_organization: alum.current_organization || '',
      designation: alum.designation || '',
      location: alum.location || '',
      linkedin_url: alum.linkedin_url || '',
      package_lpa: alum.package_lpa || ''
    });
    setShowAddModal(true);
  };

  const statusColors = {
    employed: 'bg-green-100 text-green-800',
    higher_studies: 'bg-blue-100 text-blue-800',
    entrepreneur: 'bg-purple-100 text-purple-800',
    unknown: 'bg-gray-100 text-gray-800'
  };

  const StatCard = ({ title, value, icon: Icon, color = 'blue', subtitle }) => (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
            {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
          </div>
          <div className={`w-12 h-12 bg-${color}-100 rounded-full flex items-center justify-center`}>
            <Icon className={`h-6 w-6 text-${color}-600`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="p-6 space-y-6" data-testid="alumni-management-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Alumni Management</h1>
          <p className="text-gray-500">Track alumni progression and engagement</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => { fetchStudents(); setShowConvertModal(true); }}>
            <Upload className="h-4 w-4 mr-2" />
            Convert Students
          </Button>
          <Button onClick={() => { resetForm(); setEditingAlumni(null); setShowAddModal(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            Add Alumni
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <StatCard title="Total Alumni" value={stats.total_alumni} icon={Users} color="blue" />
          <StatCard title="Employed" value={stats.employed_count} icon={Briefcase} color="green" />
          <StatCard title="Higher Studies" value={stats.higher_studies_count} icon={BookOpen} color="purple" />
          <StatCard title="Entrepreneurs" value={stats.entrepreneur_count} icon={Award} color="orange" />
          <StatCard 
            title="Progression Rate" 
            value={stats.progression_rate} 
            icon={TrendingUp} 
            color="cyan" 
          />
          <StatCard 
            title="Avg Package" 
            value={`₹${stats.package_stats?.average_lpa || 0} LPA`} 
            icon={TrendingUp} 
            color="pink"
            subtitle={`Max: ₹${stats.package_stats?.highest_lpa || 0} LPA`}
          />
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(value) => {
        setActiveTab(value);
        setSearchParams({ tab: value });
      }}>
        <TabsList>
          <TabsTrigger value="list">Alumni List</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="mt-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name, email, organization..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="employed">Employed</SelectItem>
                <SelectItem value="higher_studies">Higher Studies</SelectItem>
                <SelectItem value="entrepreneur">Entrepreneur</SelectItem>
                <SelectItem value="unknown">Unknown</SelectItem>
              </SelectContent>
            </Select>
            <Select value={yearFilter} onValueChange={setYearFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                {[...Array(10)].map((_, i) => {
                  const year = new Date().getFullYear() - i;
                  return <SelectItem key={year} value={year.toString()}>{year}</SelectItem>;
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Alumni Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left">Name</th>
                      <th className="px-4 py-3 text-left">Course</th>
                      <th className="px-4 py-3 text-center">Passing Year</th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-left">Organization</th>
                      <th className="px-4 py-3 text-right">Package</th>
                      <th className="px-4 py-3 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="7" className="text-center py-8">
                          <RefreshCw className="h-6 w-6 animate-spin mx-auto text-gray-400" />
                        </td>
                      </tr>
                    ) : alumni.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center py-8 text-gray-500">
                          No alumni found. Add alumni or convert passed out students.
                        </td>
                      </tr>
                    ) : (
                      alumni.map((alum) => (
                        <tr key={alum.id} className="border-t hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium">{alum.full_name}</p>
                              <p className="text-xs text-gray-500">{alum.email}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3">{alum.course_name || '-'}</td>
                          <td className="px-4 py-3 text-center">{alum.passing_year || '-'}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[alum.current_status] || statusColors.unknown}`}>
                              {alum.current_status?.replace('_', ' ') || 'Unknown'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {alum.current_organization && (
                              <div>
                                <p className="font-medium">{alum.current_organization}</p>
                                <p className="text-xs text-gray-500">{alum.designation}</p>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {alum.package_lpa ? `₹${alum.package_lpa} LPA` : '-'}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => openEditModal(alum)} title="Edit">
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleShareCredentials(alum)}
                                title="Share via WhatsApp"
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              >
                                <MessageCircle className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleResetPassword(alum)}
                                title="Reset Password"
                                className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                              >
                                <Key className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDelete(alum.id)} title="Delete">
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Organizations */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Top Recruiters
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.top_organizations?.length > 0 ? (
                  <div className="space-y-3">
                    {stats.top_organizations.map((org, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold text-sm">
                            {i + 1}
                          </span>
                          <span className="font-medium">{org.name}</span>
                        </div>
                        <span className="text-lg font-bold">{org.count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">No data available</p>
                )}
              </CardContent>
            </Card>

            {/* Status Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Status Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.status_wise && Object.keys(stats.status_wise).length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(stats.status_wise).map(([status, count]) => (
                      <div key={status} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[status] || statusColors.unknown}`}>
                          {status.replace('_', ' ')}
                        </span>
                        <span className="text-lg font-bold">{count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">No data available</p>
                )}
              </CardContent>
            </Card>

            {/* Year-wise */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GraduationCap className="h-5 w-5" />
                  Year-wise Alumni
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.year_wise && Object.keys(stats.year_wise).length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {Object.entries(stats.year_wise).slice(0, 12).map(([year, count]) => (
                      <div key={year} className="text-center p-4 bg-gray-50 rounded-lg">
                        <p className="text-2xl font-bold text-blue-600">{count}</p>
                        <p className="text-sm text-gray-500">{year}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">No data available</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingAlumni ? 'Edit Alumni' : 'Add New Alumni'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Full Name *</label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium">Email *</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium">Password {!editingAlumni && '*'}</label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder={editingAlumni ? "Leave blank to keep current" : "Set login password"}
                  required={!editingAlumni}
                />
                <p className="text-xs text-gray-500 mt-1">Alumni will use this to login to Alumni Portal</p>
              </div>
              <div>
                <label className="text-sm font-medium">Phone</label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Gender</label>
                <Select value={formData.gender} onValueChange={(v) => setFormData({...formData, gender: v})}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Course</label>
                <Input
                  value={formData.course_name}
                  onChange={(e) => setFormData({...formData, course_name: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Passing Year</label>
                <Input
                  type="number"
                  value={formData.passing_year}
                  onChange={(e) => setFormData({...formData, passing_year: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Roll Number</label>
                <Input
                  value={formData.roll_number}
                  onChange={(e) => setFormData({...formData, roll_number: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Registration No.</label>
                <Input
                  value={formData.registration_number}
                  onChange={(e) => setFormData({...formData, registration_number: e.target.value})}
                />
              </div>
            </div>

            <hr />
            <p className="font-medium">Current Status</p>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Status *</label>
                <Select value={formData.current_status} onValueChange={(v) => setFormData({...formData, current_status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unknown">Unknown</SelectItem>
                    <SelectItem value="employed">Employed</SelectItem>
                    <SelectItem value="higher_studies">Higher Studies</SelectItem>
                    <SelectItem value="entrepreneur">Entrepreneur</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Organization</label>
                <Input
                  value={formData.current_organization}
                  onChange={(e) => setFormData({...formData, current_organization: e.target.value})}
                  placeholder="Company / University name"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Designation</label>
                <Input
                  value={formData.designation}
                  onChange={(e) => setFormData({...formData, designation: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Location</label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Package (LPA)</label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.package_lpa}
                  onChange={(e) => setFormData({...formData, package_lpa: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium">LinkedIn URL</label>
                <Input
                  value={formData.linkedin_url}
                  onChange={(e) => setFormData({...formData, linkedin_url: e.target.value})}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button type="submit">
                {editingAlumni ? 'Update' : 'Add'} Alumni
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Convert Students Modal */}
      <Dialog open={showConvertModal} onOpenChange={setShowConvertModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Convert Students to Alumni</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Passing Year *</label>
              <Input
                type="number"
                value={passingYear}
                onChange={(e) => setPassingYear(e.target.value)}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Select Students ({selectedStudents.length} selected)</label>
              <div className="border rounded-lg max-h-[300px] overflow-y-auto mt-2">
                {students.length === 0 ? (
                  <p className="text-center py-4 text-gray-500">No active students found</p>
                ) : (
                  students.map((student) => (
                    <label
                      key={student.id}
                      className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                    >
                      <input
                        type="checkbox"
                        checked={selectedStudents.includes(student.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedStudents([...selectedStudents, student.id]);
                          } else {
                            setSelectedStudents(selectedStudents.filter(id => id !== student.id));
                          }
                        }}
                        className="w-4 h-4"
                      />
                      <div>
                        <p className="font-medium">{student.full_name || student.name}</p>
                        <p className="text-xs text-gray-500">
                          {student.student_id || student.roll_number} • {student.course_name}
                        </p>
                      </div>
                    </label>
                  ))
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleConvertStudents} disabled={selectedStudents.length === 0}>
              Convert {selectedStudents.length} Students
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credentials Modal */}
      <Dialog open={showCredentialsModal} onOpenChange={setShowCredentialsModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-green-600">Alumni Created Successfully!</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-600">Share these login credentials with the alumni for portal access:</p>
            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Email:</span>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm">{credentials?.email}</span>
                  <button onClick={() => copyToClipboard(credentials?.email)} className="p-1 hover:bg-gray-200 rounded">
                    <Copy className="h-4 w-4 text-gray-500" />
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Password:</span>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm">{credentials?.password}</span>
                  <button onClick={() => copyToClipboard(credentials?.password)} className="p-1 hover:bg-gray-200 rounded">
                    <Copy className="h-4 w-4 text-gray-500" />
                  </button>
                </div>
              </div>
            </div>
            <p className="text-xs text-amber-600 bg-amber-50 p-3 rounded-lg">
              ⚠️ Please save these credentials. The password cannot be retrieved later.
            </p>
            
            {/* WhatsApp Share Button */}
            <Button
              onClick={() => shareViaWhatsApp(
                credentials?.email, 
                credentials?.password, 
                credentials?.alumniName || 'Alumni',
                credentials?.phone
              )}
              className="w-full bg-green-500 hover:bg-green-600 text-white"
              data-testid="alumni-whatsapp-share-btn"
            >
              <MessageCircle className="h-5 w-5 mr-2" />
              Send via WhatsApp
            </Button>
            
            <Button
              variant="outline"
              onClick={() => setShowCredentialsModal(false)}
              className="w-full"
            >
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={showResetPasswordModal} onOpenChange={setShowResetPasswordModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Key className="h-5 w-5" /> Reset Alumni Password
            </DialogTitle>
          </DialogHeader>
          
          {!resetPasswordResult ? (
            <div className="space-y-4">
              <p className="text-gray-600">
                Are you sure you want to reset password for <strong>{resetPasswordAlumni?.full_name}</strong>?
              </p>
              <p className="text-sm text-gray-500">
                A new password will be generated automatically.
              </p>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowResetPasswordModal(false)}>
                  Cancel
                </Button>
                <Button onClick={confirmResetPassword} className="bg-amber-600 hover:bg-amber-700">
                  Reset Password
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-green-600 font-medium">Password reset successfully!</p>
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Name:</span>
                  <span className="font-medium">{resetPasswordResult.name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Email:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm">{resetPasswordResult.email}</span>
                    <button onClick={() => copyToClipboard(resetPasswordResult.email)} className="p-1 hover:bg-gray-200 rounded">
                      <Copy className="h-4 w-4 text-gray-500" />
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">New Password:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-bold text-green-600">{resetPasswordResult.password}</span>
                    <button onClick={() => copyToClipboard(resetPasswordResult.password)} className="p-1 hover:bg-gray-200 rounded">
                      <Copy className="h-4 w-4 text-gray-500" />
                    </button>
                  </div>
                </div>
              </div>
              <p className="text-xs text-amber-600 bg-amber-50 p-3 rounded-lg">
                ⚠️ Please share this new password with the alumni. It cannot be retrieved later.
              </p>
              
              <Button
                onClick={() => shareViaWhatsApp(
                  resetPasswordResult.email, 
                  resetPasswordResult.password, 
                  resetPasswordResult.name,
                  resetPasswordResult.phone
                )}
                className="w-full bg-green-500 hover:bg-green-600 text-white"
              >
                <MessageCircle className="h-5 w-5 mr-2" />
                Send via WhatsApp
              </Button>
              
              <Button
                variant="outline"
                onClick={() => {
                  setShowResetPasswordModal(false);
                  setResetPasswordResult(null);
                }}
                className="w-full"
              >
                Done
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
